import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { UsersMetaSortCol, TUserMetaListResponse, TUsersMetaQuery, TUsersMetaSortCol, adaptUserMetaFromListResponse, TUserMetaList, adaptUsersMetaQueryToRequest } from '../../types';
import { BACKEND_SOURCE_USERS } from '../../backend-paths';


export const usersMetaGetApi = createAsyncThunk(
	BACKEND_SOURCE_USERS,
	(payload: TUsersMetaQuery | undefined, thunkApi) => {
		const params = adaptUsersMetaQueryToRequest(payload);

		return HttpService.get<TUserMetaListResponse>(BACKEND_SOURCE_USERS, { params }).then(result => {
			return adaptUserMetaFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean,
	list: TUserMetaList,
	query: TUsersMetaQuery,
	error?: HttpError,
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: UsersMetaSortCol.CREATED_AT,
	},
	error: undefined,
};

export const usersMetaSlice = createSlice({
	name: 'usersMetaSlice',
	initialState,
	reducers: {
		setUserMetasQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setUserMetasQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setUserMetasQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setUserMetasQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setUserMetasQuerySortCol(state, action: PayloadAction<TUsersMetaSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearUsersMeta(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[usersMetaGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[usersMetaGetApi.fulfilled.type]: (state, action: PayloadAction<TUserMetaList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[usersMetaGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useUsersMetaSelector = () => useAppSelector(state => state[usersMetaSlice.name]);

export const { setUserMetasQueryLimit, setUserMetasQueryOffset, setUserMetasQuerySort, setUserMetasQuerySortCol, setUserMetasQuerySearch, clearUsersMeta } = usersMetaSlice.actions;
